/**
 * QuickViewModal custom element.
 *
 * Loaded as a separate JS asset so the class is registered during the initial
 * page parse — before product-recommendations.js fires its innerHTML injection.
 * connectedCallback() uses setTimeout(0) to defer _init() until after the HTML
 * parser has finished the element's children (needed for elements parsed in
 * normal HTML flow where children are not yet available at the opening tag).
 * For innerHTML-injected elements all children are already present, so the
 * defer is harmless.
 *
 * Liquid values (money format, translations) are passed as data-attributes on
 * the <quick-view-modal> element instead of being baked into this file.
 */
if (!customElements.get('quick-view-modal')) {
  class QuickViewModal extends HTMLElement {
    connectedCallback() {
      setTimeout(() => this._init(), 0);
    }

    _init() {
      if (this._initialized) return;

      const sectionId = this.dataset.productId;
      if (!sectionId) return;

      // Deduplication: If another <quick-view-modal> for this product is already present in the DOM
      // (e.g. desktop grid vs mobile carousel rendering), remove this duplicate element.
      const existingModal = document.querySelector(`quick-view-modal[data-product-id="${sectionId}"]`);
      if (existingModal && existingModal !== this) {
        this.remove();
        return;
      }

      const popup = this.querySelector(`#QuickViewPopup-${sectionId}`);
      if (!popup) return;

      // Clean up any orphaned popup with this ID in document.body (e.g. from previous dynamic fetches)
      const orphanPopup = document.body.querySelector(`#QuickViewPopup-${sectionId}`);
      if (orphanPopup && orphanPopup !== popup) {
        orphanPopup.remove();
      }

      this._initialized = true;

      const moneyFormat   = this.dataset.moneyFormat   || '${{amount}}';
      const textAddToCart = this.dataset.textAddToCart || 'Add to cart';
      const textSoldOut   = this.dataset.textSoldOut   || 'Sold out';
      const textUnavail   = this.dataset.textUnavail   || 'Unavailable';

      const overlay      = popup.querySelector('.quick-view-popup__overlay');
      const closeBtn     = popup.querySelector('.quick-view-popup__close');
      const mediaImg     = popup.querySelector(`#PopupMedia-${sectionId}`);
      const atcBtn       = popup.querySelector('button.quick-view-popup__atc-btn');
      const variantInput = popup.querySelector('input[name="id"]');
      const productJson  = JSON.parse(
        this.querySelector(`#ProductJson-${sectionId}`).textContent
      );

      // Close
      const closePopup = () => {
        popup.setAttribute('data-active', 'false');
        document.body.style.overflow = '';
      };
      closeBtn.addEventListener('click', closePopup);
      overlay.addEventListener('click', closePopup);

      // Option selection
      popup.querySelectorAll('.quick-view-popup__option-value').forEach((btn) => {
        btn.addEventListener('click', () => {
          const group = btn.closest('.quick-view-popup__option-group');
          group.querySelectorAll('.quick-view-popup__option-value')
               .forEach((b) => b.setAttribute('data-selected', 'false'));
          btn.setAttribute('data-selected', 'true');
          group.querySelector('.quick-view-popup__option-label span').textContent =
            btn.dataset.value;
          updateVariant();
        });
      });

      // Money formatting
      const formatMoney = (cents, format) => {
        if (typeof cents === 'string') cents = cents.replace('.', '');
        const fmt = format || '${{amount}}';
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const match = fmt.match(placeholderRegex);
        if (!match) return cents;
        const fn = (n, p = 2, t = ',', d = '.') => {
          if (isNaN(n) || n == null) return 0;
          n = (n / 100.0).toFixed(p);
          const [dollars, cents] = n.split('.');
          return dollars.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t) +
                 (cents ? d + cents : '');
        };
        let value = '';
        switch (match[1]) {
          case 'amount':                                  value = fn(cents, 2);          break;
          case 'amount_no_decimals':                      value = fn(cents, 0);          break;
          case 'amount_with_comma_separator':             value = fn(cents, 2, '.', ','); break;
          case 'amount_no_decimals_with_comma_separator': value = fn(cents, 0, '.', ','); break;
        }
        return fmt.replace(placeholderRegex, value);
      };

      // Variant update
      const updateVariant = () => {
        const selectedOptions = Array.from(
          popup.querySelectorAll('.quick-view-popup__option-value[data-selected="true"]')
        ).map((b) => b.dataset.value);

        const matchedVariant = productJson.variants.find((v) =>
          v.options.every((opt, i) => opt === selectedOptions[i])
        );

        if (matchedVariant) {
          variantInput.value = matchedVariant.id;

          const priceNew    = popup.querySelector('.quick-view-popup__price-new');
          const priceOld    = popup.querySelector('.quick-view-popup__price-old');
          const variantData = JSON.parse(
            this.querySelector(`#VariantMetafields-${sectionId}`).textContent
          );
          const vd = variantData.variants[matchedVariant.id].discount;
          const pd = variantData.productDiscount;
          const finalDiscount = (vd && vd != 0) ? vd : (pd && pd != 0 ? pd : null);

          if (finalDiscount) {
            priceOld.innerHTML = formatMoney(matchedVariant.price, moneyFormat);
            priceOld.style.display = 'inline';
            priceNew.innerHTML = formatMoney(finalDiscount * 100, moneyFormat);
            priceNew.setAttribute('data-discount', 'true');
          } else {
            priceOld.style.display = 'none';
            priceNew.innerHTML = formatMoney(matchedVariant.price, moneyFormat);
            priceNew.setAttribute('data-discount', 'false');
          }

          if (matchedVariant.featured_image) {
            let src = matchedVariant.featured_image.src;
            if (!src.startsWith('http')) src = 'https:' + src;
            src = src.indexOf('?') > -1
              ? src.split('?')[0] + '?width=600&v=' + matchedVariant.featured_image.id
              : src + '?width=600';
            mediaImg.src = src;
          }

          if (matchedVariant.available) {
            atcBtn.disabled = false;
            atcBtn.querySelector('.add-to-cart-icon')?.closest('.svg-wrapper')?.removeAttribute('hidden');
            atcBtn.querySelector('.add-to-cart-text__content span span').textContent = textAddToCart;
          } else {
            atcBtn.disabled = true;
            atcBtn.querySelector('.add-to-cart-icon')?.closest('.svg-wrapper')?.setAttribute('hidden', '');
            atcBtn.querySelector('.add-to-cart-text__content span span').textContent = textSoldOut;
          }
        } else {
          atcBtn.disabled = true;
          atcBtn.querySelector('.add-to-cart-icon')?.closest('.svg-wrapper')?.setAttribute('hidden', '');
          atcBtn.querySelector('.add-to-cart-text__content span span').textContent = textUnavail;
        }
      };

      // AJAX Add to Cart
      popup.querySelector('form').addEventListener('submit', (e) => {
        e.preventDefault();
        atcBtn.disabled = true;
        fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
          body: JSON.stringify({
            id: variantInput.value,
            quantity: 1,
            sections: 'cart-notification-product,cart-notification-button,cart-icon-bubble'
          })
        })
          .then((res) => res.json())
          .then((data) => {
            atcBtn.setAttribute('data-added', 'true');
            setTimeout(() => { atcBtn.removeAttribute('data-added'); atcBtn.disabled = false; }, 1600);
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: data,
                sourceId: 'quick-view-modal',
                data: { itemCount: 1, source: 'product-form-component' }
              }
            }));
          })
          .catch((err) => { console.error(err); atcBtn.disabled = false; });
      });

      // Media slider
      const mediaItems = productJson.images;
      let currentMediaIndex = 0;
      const updateMedia = (index) => {
        currentMediaIndex = (index + mediaItems.length) % mediaItems.length;
        let src = mediaItems[currentMediaIndex];
        if (!src.startsWith('http')) src = 'https:' + src;
        src = src.indexOf('?') > -1
          ? src.split('?')[0] + '?width=600'
          : src + '?width=600';
        mediaImg.src = src;
      };
      const prevBtn = popup.querySelector('.prev');
      const nextBtn = popup.querySelector('.next');
      if (prevBtn) prevBtn.addEventListener('click', () => updateMedia(currentMediaIndex - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => updateMedia(currentMediaIndex + 1));

      // Open trigger — delegated on document so it works regardless of render order
      document.addEventListener('click', (e) => {
        const btn = e.target.closest(`.quickview[data-product-id="${sectionId}"]`);
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();

        // Close any other open popups first
        document.querySelectorAll('.quick-view-popup[data-active="true"]').forEach((p) => {
          p.setAttribute('data-active', 'false');
        });

        if (popup.parentElement !== document.body) document.body.appendChild(popup);
        popup.setAttribute('data-active', 'true');
        document.body.style.overflow = 'hidden';
      });

      // Escape key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && popup.getAttribute('data-active') === 'true') {
          closePopup();
        }
      });
    }
  }

  customElements.define('quick-view-modal', QuickViewModal);
}
